/**
 * 用户追踪系统 - 防线1：路由会话去重
 * 
 * 架构说明：
 * 防线1（前端）：路由会话去重 - 减少无效请求，节省带宽
 * 防线2（后端）：停留时长过滤 - 过滤 < 2秒的垃圾数据
 * 防线3（API）：限流管理 - 每个session每分钟最多10次请求
 */

import axios from 'axios';

/**
 * 路由会话对象接口
 */
export interface RouteSession {
  url: string;              // 当前路由 URL
  enterTime: number;        // 进入时间戳（毫秒）
  sessionId: string;        // 会话 ID
}

/**
 * 访问记录接口
 */
export interface VisitRecord {
  url: string;              // 页面URL
  stay_duration: number;    // 停留时长（秒）
  session_id: string;       // 会话ID
  page_title?: string;      // 页面标题
  referrer?: string;        // 页面来源
}

/**
 * 用户追踪服务
 * 实现三道防线的前端部分（防线1）
 */
class TrackingService {
  private routeSession: RouteSession = {
    url: '',
    enterTime: Date.now(),
    sessionId: this.generateSessionId(),
  };

  private lastRecordedRoute: string = '';
  private lastRecordedSession: string = '';
  private apiBaseUrl: string;
  private minStayDuration: number = 2; // 前端预验证：最少停留秒数

  constructor(apiBaseUrl?: string) {
    // 自动判断后端地址
    // 1. 优先使用传入参数
    // 2. 其次使用环境变量 VITE_API_BASE_URL
    // 3. 如果都没有，默认使用相对路径 '/api/v1'（走 Vite Proxy）
    const base = apiBaseUrl || import.meta.env.VITE_API_BASE_URL || '';
    
    if (base) {
      this.apiBaseUrl = base.endsWith('/api/v1') ? base : `${base}/api/v1`;
    } else {
      this.apiBaseUrl = '/api/v1';
    }

    console.log('[Tracking] 追踪服务初始化', {
      sessionId: this.routeSession.sessionId,
      apiBaseUrl: this.apiBaseUrl,
    });

    // ✅ 监听页面刷新或关闭，发送最后一次访问统计
    window.addEventListener('beforeunload', () => {
      this.handlePageExit();
    });
  }

  /**
   * 处理页面退出/切换逻辑
   * 如果停留时长超过 2 秒，则发送记录
   */
  private handlePageExit(pageTitle?: string, referrer?: string): void {
    const now = Date.now();
    const stayDuration = Math.floor((now - this.routeSession.enterTime) / 1000);

    if (this.routeSession.url && stayDuration >= this.minStayDuration) {
      const isDuplicate =
        this.lastRecordedRoute === this.routeSession.url &&
        this.lastRecordedSession === this.routeSession.sessionId;

      if (!isDuplicate) {
        this.recordVisit({
          url: this.routeSession.url,
          stay_duration: stayDuration,
          session_id: this.routeSession.sessionId,
          page_title: pageTitle,
          referrer: referrer,
        });

        this.lastRecordedRoute = this.routeSession.url;
        this.lastRecordedSession = this.routeSession.sessionId;
      }
    }
  }

  /**
   * 生成唯一的会话ID
   * 格式: {timestamp}-{random}
   */
  private generateSessionId(): string {
    const timestamp = Date.now();
    const random = Math.random().toString(36).substring(2, 9);
    const sessionId = `${timestamp}-${random}`;
    return sessionId;
  }

  /**
   * 获取当前会话ID
   */
  public getSessionId(): string {
    return this.routeSession.sessionId;
  }

  /**
   * 更新路由 - 在路由变化时调用
   * 
   * @param pageUrl - 新路由的URL
   * @param pageTitle - 页面标题（可选）
   * @param referrer - 页面来源（可选）
   */
  public updateRoute(
    pageUrl: string,
    pageTitle?: string,
    referrer?: string
  ): void {
    // 1. 处理前一个页面的退出记录
    this.handlePageExit(pageTitle, referrer);

    // 2. 开启新路由的会话计时
    this.routeSession = {
      url: pageUrl,
      enterTime: Date.now(),
      sessionId: this.routeSession.sessionId,
    };
    
    console.log('[Tracking] 路由已更新', {
      newUrl: pageUrl,
      sessionId: this.routeSession.sessionId
    });
  }

  /**
   * 发送访问记录到后端
   * 
   * 调用 API：POST /api/v1/tracking/record_visit
   * 
   * @param visitData - 访问记录数据
   */
  private async recordVisit(visitData: VisitRecord): Promise<void> {
    try {
      console.log('[Tracking] 发送访问记录', visitData);

      const response = await axios.post(
        `${this.apiBaseUrl}/tracking/record_visit`,
        visitData,
        {
          timeout: 5000, // 5秒超时
        }
      );

      if (response.data?.success) {
        console.log('[Tracking] 访问记录已成功发送');
      } else {
        console.warn('[Tracking] 记录失败', response.data);
      }
    } catch (error) {
      if (axios.isAxiosError(error)) {
        if (error.response?.status === 429) {
          console.warn('[Tracking] 请求被限流 (429)', {
            sessionId: visitData.session_id,
          });
        } else {
          console.error('[Tracking] 网络错误', {
            status: error.response?.status,
            message: error.message,
          });
        }
      } else {
        console.error('[Tracking] 发送记录时出错', error);
      }
      // 不中断应用流程 - 追踪失败不影响用户体验
    }
  }

  /**
   * 记录自定义事件（预留接口）
   * 
   * @param eventType - 事件类型
   * @param eventData - 事件数据
   */
  public recordEvent(eventType: string, eventData: Record<string, any> = {}): void {
    console.log('[Tracking] 记录自定义事件', {
      sessionId: this.routeSession.sessionId,
      eventType,
      eventData,
    });
    // TODO: 实现事件记录逻辑
  }

  /**
   * 获取当前路由会话信息
   */
  public getCurrentSession(): RouteSession {
    return { ...this.routeSession };
  }

  /**
   * 重置会话（新用户或会话过期时调用）
   */
  public resetSession(): void {
    const now = Date.now();
    this.routeSession = {
      url: '',
      enterTime: now,
      sessionId: this.generateSessionId(),
    };
    this.lastRecordedRoute = '';
    this.lastRecordedSession = '';
    console.log('[Tracking] 会话已重置', {
      newSessionId: this.routeSession.sessionId,
    });
  }

  /**
   * 用户登出时的清理
   */
  public onLogout(): void {
    console.log('[Tracking] 用户登出，重置追踪会话');
    this.resetSession();
  }
}

// 导出单例实例
export const trackingService = new TrackingService();

export default TrackingService;
